import {
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import DrawerMenu from 'components/DrawerMenu';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import ArrowRightAltRoundedIcon from '@mui/icons-material/ArrowRightAltRounded';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import CompareArrowsRoundedIcon from '@mui/icons-material/CompareArrowsRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CurrencyRupeeOutlinedIcon from '@mui/icons-material/CurrencyRupeeOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PriceCheckOutlinedIcon from '@mui/icons-material/PriceCheckOutlined';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import strings from '../../../../common/Translation/Translate';
import Tabular from '../../../../components/Tabular/Tabular';
import axiosInstance from '../../../../utils/axios';
import { URLS } from '../../../../utils/constants/urls';
import { getPricePackageName } from 'utils/pricePackage';
import '../styles.scss';

dayjs.extend(advancedFormat);

interface BookingHistoryProps {
  bookingLineItem: any;
}

const BookingHistory = ({ bookingLineItem }: BookingHistoryProps) => {
  const [historyPage, setHistoryPage] = useState<any>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [page, setPage] = useState(1);

  const fetchHistory = async (opts?: { nextPage?: number }) => {
    if (!bookingLineItem?.id) return;

    const nextPage = opts?.nextPage ?? page;
    const filter: any[] = [];

    try {
      setHistoryLoading(true);
      const { data } = await axiosInstance.post(URLS.bookingLineItemHistoryPage(bookingLineItem.id), {
        page: nextPage,
        size: 10,
        sort: ['createdAt:desc'],
        filter,
      });
      setHistoryPage(data);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    // Reset when switching line item
    setPage(1);
  }, [bookingLineItem?.id]);

  useEffect(() => {
    fetchHistory({ nextPage: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingLineItem?.id]);

  const historyRows = (historyPage?.content ?? []) as any[];
  const totalPages = historyPage?.totalPages ?? 1;
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareItem, setCompareItem] = useState<any>(null);
  const [showAllCompareFields, setShowAllCompareFields] = useState(false);

  const safeParseJson = (value: unknown) => {
    if (!value || typeof value !== 'string') return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  };

  const getSnapshots = (item: any) => {
    const oldSnap = safeParseJson(item?.oldSnapshotJson);
    const newSnap = safeParseJson(item?.newSnapshotJson);
    return { oldSnap, newSnap };
  };

  const compareData = useMemo(() => {
    if (!compareItem) return null;
    const { oldSnap, newSnap } = getSnapshots(compareItem);
    if (!oldSnap && !newSnap) return null;
    return { oldSnap: oldSnap ?? {}, newSnap: newSnap ?? {} };
  }, [compareItem]);

  const diffFields = [
    { key: 'guaranteedGuests', label: strings.bookingHistoryTabsGuests },
    {
      key: bookingLineItem?.hourlyPricing ? 'pricePerHour' : 'pricePerPerson',
      label: bookingLineItem?.hourlyPricing ? strings.bookingHistoryTabsPricePerHour : strings.bookingHistoryTabsPricePerPerson,
    },
    { key: 'minimumRevenue', label: strings.bookingHistoryTabsMinimumRevenue },
    { key: 'hallRental', label: strings.bookingHistoryTabsHallRental },
    { key: 'actualTotalCost', label: strings.bookingHistoryTabsActualCost },
    { key: 'pricePackage', label: 'Price Package' },
    { key: 'serviceCharge', label: 'Service Charge' },
    { key: 'holdDays', label: 'Hold Days' },
    { key: 'eventSubtype', label: 'Event Subtype' },
    { key: 'layout', label: 'Layout' },
    { key: 'placeOfEvent', label: 'Place of Event' },
    { key: 'addressOfEvent', label: 'Address of Event' },
    { key: 'startDate', label: 'Start Date' },
    { key: 'endDate', label: 'End Date' },
    { key: 'startTime', label: 'Start Time' },
    { key: 'endTime', label: 'End Time' },
    { key: 'partOfDay', label: 'Part of Day' },
    { key: 'preLeadTime', label: 'Pre Lead Time' },
    { key: 'postLeadTime', label: 'Post Lead Time' },
    { key: 'taxOnGuestCharges', label: 'Tax on Guest Charges' },
    { key: 'taxOnHallRental', label: 'Tax on Hall Rental' },
    { key: 'addOns', label: 'Add-ons' },
    { key: 'additionalComments', label: 'Additional Comments' },
  ];

  const formatPricePackage = (v: any) => {
    return getPricePackageName({ pricePackage: v }, '-');
  };

  const formatLayout = (v: any) => {
    if (!v) return '-';
    const name = v?.name ?? '-';
    return `${name}`;
  };

  const normalizeTaxes = (v: any) => {
    if (!Array.isArray(v)) return [];
    return v
      .map(t => ({
        id: t?.id ?? null,
        name: t?.name ?? null,
        percentage: t?.percentage ?? null,
      }))
      .filter(t => t.id !== null)
      .sort((a, b) => Number(a.id) - Number(b.id));
  };

  const formatTaxes = (v: any) => {
    const taxes = normalizeTaxes(v);
    if (!taxes.length) return '-';
    return taxes.map(t => `${t.name ?? 'Tax'}`).join(', ');
  };

  const taxIdsKey = (v: any) => normalizeTaxes(v).map(t => t.id).join(',');

  const normalizeAddOns = (v: any) => {
    if (!Array.isArray(v)) return [];
    return v
      .map(a => ({
        addOnId: a?.addOnId ?? null,
        addOnName: a?.addOnName ?? null,
        addOnCategory: a?.addOnCategory ?? null,
        addOnType: a?.addOnType ?? null,
        quantity: a?.quantity ?? null,
        price: a?.price ?? null,
      }))
      .filter(a => a.addOnId !== null)
      .sort((a, b) => {
        const an = String(a.addOnName ?? '').localeCompare(String(b.addOnName ?? ''));
        if (an !== 0) return an;
        return Number(a.addOnId) - Number(b.addOnId);
      });
  };

  const addOnKey = (a: any) => `${a.addOnName ?? a.addOnCategory ?? 'Add-on'}`;

  const formatAddOns = (v: any) => {
    const addOns = normalizeAddOns(v);
    if (!addOns.length) return '-';
    return addOns
      .map(a => `${addOnKey(a)} — qty: ${a.quantity ?? '-'}, price: ${a.price ?? '-'}`)
      .sort((a, b) => a.localeCompare(b))
      .join('\n');
  };

  const diffAddOnsText = (oldVal: any, newVal: any) => {
    const oldList = normalizeAddOns(oldVal);
    const newList = normalizeAddOns(newVal);
    const oldById = new Map(oldList.map(a => [a.addOnId, a]));
    const newById = new Map(newList.map(a => [a.addOnId, a]));

    const lines: string[] = [];
    const allIds = Array.from(new Set([...oldById.keys(), ...newById.keys()])).sort((a, b) => Number(a) - Number(b));

    for (const id of allIds) {
      const o = oldById.get(id);
      const n = newById.get(id);
      if (o && !n) {
        lines.push(`- Removed: ${addOnKey(o)}`);
        continue;
      }
      if (!o && n) {
        lines.push(`+ Added: ${addOnKey(n)} — qty: ${n.quantity ?? '-'}, price: ${n.price ?? '-'}`);
        continue;
      }
      if (o && n) {
        const qtyChanged = o.quantity !== n.quantity;
        const priceChanged = o.price !== n.price;
        if (qtyChanged || priceChanged) {
          lines.push(
            `~ Updated: ${addOnKey(n)} — qty: ${o.quantity ?? '-'} → ${n.quantity ?? '-'}, price: ${o.price ?? '-'} → ${n.price ?? '-'}`
          );
        }
      }
    }

    return lines.length ? lines.join('\n') : '-';
  };

  const computeAddOnsDiff = (oldVal: any, newVal: any) => {
    const oldList = normalizeAddOns(oldVal);
    const newList = normalizeAddOns(newVal);
    const oldById = new Map(oldList.map(a => [a.addOnId, a]));
    const newById = new Map(newList.map(a => [a.addOnId, a]));

    const added: any[] = [];
    const removed: any[] = [];
    const updated: { oldItem: any; newItem: any; qtyDelta: number | null; priceDelta: number | null }[] = [];
    const unchanged: any[] = [];

    const allIds = Array.from(new Set([...oldById.keys(), ...newById.keys()])).sort((a, b) => Number(a) - Number(b));
    for (const id of allIds) {
      const o = oldById.get(id);
      const n = newById.get(id);
      if (o && !n) {
        removed.push(o);
        continue;
      }
      if (!o && n) {
        added.push(n);
        continue;
      }
      if (o && n) {
        const qtyChanged = o.quantity !== n.quantity;
        const priceChanged = o.price !== n.price;
        if (qtyChanged || priceChanged) {
          const qtyDelta =
            typeof o.quantity === 'number' && typeof n.quantity === 'number' ? n.quantity - o.quantity : null;
          const priceDelta = typeof o.price === 'number' && typeof n.price === 'number' ? n.price - o.price : null;
          updated.push({ oldItem: o, newItem: n, qtyDelta, priceDelta });
        } else {
          unchanged.push(n);
        }
      }
    }

    const byName = (a: any, b: any) => String(a?.addOnName ?? '').localeCompare(String(b?.addOnName ?? ''));
    added.sort(byName);
    removed.sort(byName);
    unchanged.sort(byName);
    updated.sort((a, b) => byName(a.newItem, b.newItem));

    return { added, removed, updated, unchanged };
  };

  const pretty = (v: any) => {
    if (v === null || v === undefined) return '-';
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
    try {
      return JSON.stringify(v, null, 2);
    } catch {
      return String(v);
    }
  };

  const isMeaningfullyEmpty = (v: any) => {
    if (v === null || v === undefined) return true;
    if (typeof v === 'string') return v.trim() === '' || v.trim() === '-';
    if (Array.isArray(v)) return v.length === 0;
    if (typeof v === 'object') return Object.keys(v).length === 0;
    return false;
  };

  const inlineDiff = (oldVal: any, newVal: any) => {
    const fmtNum = (n: number) => {
      if (!Number.isFinite(n)) return String(n);
      const rounded = Math.round(n * 100) / 100;
      return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
    };

    const oldText = typeof oldVal === 'number' ? fmtNum(oldVal) : pretty(oldVal);
    const newText = typeof newVal === 'number' ? fmtNum(newVal) : pretty(newVal);

    const oldNum = typeof oldVal === 'number' ? oldVal : Number.NaN;
    const newNum = typeof newVal === 'number' ? newVal : Number.NaN;
    const isNumeric = Number.isFinite(oldNum) && Number.isFinite(newNum);

    const changed = JSON.stringify(oldVal) !== JSON.stringify(newVal);
    if (!changed) return { changed: false, node: <Typography variant="body2" sx={{ color: 'text.secondary' }}>{newText}</Typography> };

    const delta = isNumeric ? newNum - oldNum : null;
    const deltaText = delta === null ? null : `${delta >= 0 ? '+' : ''}${fmtNum(delta)}`;
    const arrow = delta === null ? null : delta > 0 ? '↑' : delta < 0 ? '↓' : '→';
    const deltaColor = delta === null ? 'text.secondary' : delta > 0 ? 'success.main' : delta < 0 ? 'error.main' : 'text.secondary';

    return {
      changed: true,
      node: (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {oldText}
          </Typography>
          <ArrowRightAltRoundedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
          <Typography variant="body2" sx={{ color: '#3B82F6', fontWeight: 800 }}>
            {newText}
          </Typography>
          {deltaText !== null ? (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
              (
              <Typography component="span" variant="caption" sx={{ color: deltaColor, fontWeight: 900 }}>
                {deltaText}
              </Typography>
              ) {arrow}
            </Typography>
          ) : null}
        </Stack>
      ),
    };
  };

  const changedTaxes = (oldVal: any, newVal: any) => taxIdsKey(oldVal) !== taxIdsKey(newVal);
  const changedPricePackage = (oldVal: any, newVal: any) => (oldVal?.id ?? null) !== (newVal?.id ?? null);
  const changedLayout = (oldVal: any, newVal: any) => (oldVal?.id ?? null) !== (newVal?.id ?? null);
  const changedAddOns = (oldVal: any, newVal: any) =>
    JSON.stringify(normalizeAddOns(oldVal)) !== JSON.stringify(normalizeAddOns(newVal));

  const summarizeChanges = (oldSnap: any, newSnap: any) => {
    const changes: { fieldKey: string; group: string }[] = [];

    const add = (fieldKey: string, group: string, changed: boolean) => {
      if (changed) changes.push({ fieldKey, group });
    };

    add('pricing.price', 'pricing', JSON.stringify(oldSnap?.pricePerPerson) !== JSON.stringify(newSnap?.pricePerPerson) || JSON.stringify(oldSnap?.pricePerHour) !== JSON.stringify(newSnap?.pricePerHour));
    add('pricing.minimumRevenue', 'pricing', JSON.stringify(oldSnap?.minimumRevenue) !== JSON.stringify(newSnap?.minimumRevenue));
    add('pricing.serviceCharge', 'pricing', JSON.stringify(oldSnap?.serviceCharge) !== JSON.stringify(newSnap?.serviceCharge));
    add('pricing.actualTotalCost', 'pricing', JSON.stringify(oldSnap?.actualTotalCost) !== JSON.stringify(newSnap?.actualTotalCost));
    add('event.layout', 'event', changedLayout(oldSnap?.layout, newSnap?.layout));
    add('event.eventSubtype', 'event', JSON.stringify(oldSnap?.eventSubtype) !== JSON.stringify(newSnap?.eventSubtype));
    add('event.guaranteedGuests', 'event', JSON.stringify(oldSnap?.guaranteedGuests) !== JSON.stringify(newSnap?.guaranteedGuests));
    add('timing.start', 'timing', JSON.stringify(oldSnap?.startDate) !== JSON.stringify(newSnap?.startDate) || JSON.stringify(oldSnap?.startTime) !== JSON.stringify(newSnap?.startTime));
    add('timing.end', 'timing', JSON.stringify(oldSnap?.endDate) !== JSON.stringify(newSnap?.endDate) || JSON.stringify(oldSnap?.endTime) !== JSON.stringify(newSnap?.endTime));
    add('timing.partOfDay', 'timing', JSON.stringify(oldSnap?.partOfDay) !== JSON.stringify(newSnap?.partOfDay));
    add('tax.guest', 'tax', changedTaxes(oldSnap?.taxOnGuestCharges, newSnap?.taxOnGuestCharges));
    add('tax.hall', 'tax', changedTaxes(oldSnap?.taxOnHallRental, newSnap?.taxOnHallRental));
    add('addons', 'addons', changedAddOns(oldSnap?.addOns, newSnap?.addOns));

    const pricingChanges = changes.filter(c => c.group === 'pricing').length;
    const layoutChanges = changes.some(c => c.fieldKey === 'event.layout') ? 1 : 0;
    const addOnChanges = changes.some(c => c.fieldKey === 'addons') ? 1 : 0;

    return {
      totalFieldsUpdated: changes.length,
      pricingChanges,
      layoutChanges,
      addOnChanges,
    };
  };

  const renderSnapshotDiff = (oldVal: any, newVal: any) => {
    // If only old snapshot exists (event-type history), show old value as-is.
    const effectiveNew = newVal ?? oldVal;
    const isUpdated = oldVal !== undefined && newVal !== undefined && oldVal !== newVal;
    return renderValueDiff({ isUpdated, oldValue: oldVal ?? '-', newValue: effectiveNew ?? '-' });
  };

  const hasSnapshots = (item: any) => {
    const { oldSnap, newSnap } = getSnapshots(item);
    return Boolean(oldSnap || newSnap);
  };

  const renderChangeLabel = (label: string) => (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 170 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800 }}>
        {label}
      </Typography>
    </Stack>
  );

  const renderValueDiff = ({
    isUpdated,
    oldValue,
    newValue,
  }: {
    isUpdated?: boolean;
    oldValue?: string | number | null;
    newValue?: string | number | null;
  }) => {
    if (!oldValue && !newValue) return '-';

    if (!isUpdated)
      return (
        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          {oldValue ?? '-'}
        </Typography>
      );

    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {oldValue ?? '-'}
        </Typography>
        <ArrowRightAltRoundedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
        <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 700 }}>
          {newValue ?? '-'}
        </Typography>
      </Stack>
    );
  };

  const renderStatusChip = (item: any) => {
    const action = String(item?.action || '').toUpperCase();

    const fallbackLabel = (() => {
      if (!action) return '-';
      return action
        .toLowerCase()
        .split('_')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
    })();

    const label = (item?.actionText && item.actionText !== action ? item.actionText : null) ?? fallbackLabel;

    const chipConfig: Record<
      string,
      {
        fg: string;
        bg: string;
        icon: ReactNode;
      }
    > = {
      // Booking status
      CONFIRMED: { fg: '#22C55E', bg: '#DCFCE7', icon: <CheckCircleOutlineRoundedIcon fontSize="small" /> },
      TENTATIVE: { fg: '#F59E0B', bg: '#FEF3C7', icon: <HourglassEmptyRoundedIcon fontSize="small" /> },
      AWAITING_CONFIRMATION: { fg: '#fff', bg: '#A3A329', icon: <HourglassEmptyRoundedIcon fontSize="small" /> },
      ENQUIRY: { fg: '#5C6BC0', bg: '#E8EAF6', icon: <InfoOutlined fontSize="small" /> },
      CANCELLED: { fg: '#EF4444', bg: '#FEE2E2', icon: <RemoveCircleOutlineRoundedIcon fontSize="small" /> },
      WAITLISTED: { fg: '#3B82F6', bg: '#DBEAFE', icon: <PriorityHighRoundedIcon fontSize="small" /> },
      ACTUALIZED: { fg: '#8B5CF6', bg: '#EDE9FE', icon: <PriceCheckOutlinedIcon fontSize="small" /> },
      EXPIRED: { fg: '#FDBA74', bg: '#FFF7ED', icon: <EventBusyOutlinedIcon fontSize="small" /> },
      DELETED: { fg: '#9CA3AF', bg: '#F3F4F6', icon: <DeleteOutlineRoundedIcon fontSize="small" /> },

      // Edit/change events
      EDITED: { fg: '#F97316', bg: '#FFEDD5', icon: <EditOutlinedIcon fontSize="small" /> },
      CUSTOMER_DETAILS_UPDATED: { fg: '#F97316', bg: '#FFEDD5', icon: <EditOutlinedIcon fontSize="small" /> },

      // BEO actions
      BEO_CREATED: { fg: '#6366F1', bg: '#E0E7FF', icon: <DescriptionOutlinedIcon fontSize="small" /> },
      BEO_MODIFIED: { fg: '#818CF8', bg: '#E0E7FF', icon: <ReceiptLongOutlinedIcon fontSize="small" /> },
      BEO_RELEASED: { fg: '#3730A3', bg: '#E0E7FF', icon: <DescriptionOutlinedIcon fontSize="small" /> },

      // Revenue actions
      ACTUAL_REVENUE_ADDED: { fg: '#8B5CF6', bg: '#EDE9FE', icon: <CurrencyRupeeOutlinedIcon fontSize="small" /> },
      ACTUAL_REVENUE_MODIFIED: { fg: '#A78BFA', bg: '#F3E8FF', icon: <CurrencyRupeeOutlinedIcon fontSize="small" /> },
      ACTUALIZATION_FINALIZED: { fg: '#800080', bg: '#F3E5F5', icon: <PriceCheckOutlinedIcon fontSize="small" /> },

      // Payment actions
      PAYMENT_ADDED: { fg: '#14B8A6', bg: '#CCFBF1', icon: <PaymentsOutlinedIcon fontSize="small" /> },

      // BTC / Ledger entry actions
      LEDGER_ENTRY_ADDED: { fg: '#0284C7', bg: '#E0F2FE', icon: <ReceiptLongOutlinedIcon fontSize="small" /> },
    };

    const cfg = chipConfig[action];

    if (!cfg) {
      return <Chip label={label} variant="outlined" size="small" />;
    }

    return (
      <Chip
        label={label}
        size="small"
        icon={cfg.icon as any}
        sx={{
          bgcolor: cfg.bg,
          color: cfg.fg,
          fontWeight: 800,
          '& .MuiChip-icon': { color: cfg.fg },
          '& .MuiChip-label': { color: cfg.fg },
        }}
      />
    );
  };

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
        <div style={{ flex: 1 }} />

        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" variant="outlined" label={`Page ${page} / ${totalPages}`} sx={{ fontWeight: 800 }} />
          <Chip
            size="small"
            label="Prev"
            clickable
            variant="outlined"
            disabled={historyLoading || page <= 1}
            onClick={() => {
              const next = Math.max(1, page - 1);
              setPage(next);
              fetchHistory({ nextPage: next });
            }}
          />
          <Chip
            size="small"
            label="Next"
            clickable
            variant="outlined"
            disabled={historyLoading || page >= totalPages}
            onClick={() => {
              const next = Math.min(totalPages, page + 1);
              setPage(next);
              fetchHistory({ nextPage: next });
            }}
          />
        </Stack>
      </Stack>

      {historyLoading ? (
        <Typography variant="body2" sx={{ my: 2, color: 'text.secondary' }}>
          Loading history...
        </Typography>
      ) : historyRows.length > 0 ? (
        <Tabular
          rows={[
            strings.bookingHistoryTabsUpdated,
            strings.bookingHistoryTabsEditedStatus,
            strings.bookingHistoryTabsChanges,
            strings.bookingHistoryTabsEditedReason,
          ]}
          sx={{
            'thead > tr, tbody > tr': { whiteSpace: 'nowrap' },
            'thead > tr > th:nth-of-type(1), tbody > tr > td:nth-of-type(1)': { width: '15%' },
            'thead > tr > th:nth-of-type(2), tbody > tr > td:nth-of-type(2)': { width: '15%' },
            'thead > tr > th:nth-of-type(3), tbody > tr > td:nth-of-type(3)': { width: '35%' },
            'thead > tr > th:nth-of-type(4), tbody > tr > td:nth-of-type(4)': { width: '35%' },
            '& .MuiTableCell-head': {
              fontSize: '0.75rem',
              lineHeight: 1.4,
            },
            '& .MuiTableCell-body': {
              fontSize: '0.8125rem',
              lineHeight: 1.5,
            },
            '& .MuiTypography-body2': {
              fontSize: '0.8125rem',
            },
            '& .MuiTypography-caption': {
              fontSize: '0.75rem',
            },
            '& .MuiChip-root': {
              fontSize: '0.7rem',
              height: '22px',
            },
          }}>
          {historyRows.map((item: any) => (
            <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }} key={item.id}>
              <Fragment key={item.id}>
                <TableCell scope="row">
                  <Stack spacing={0.25}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <CalendarMonthOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      <Typography variant="body2">{dayjs(item.createdAt).format('DD/MM/YYYY HH:mm')}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <PersonOutlineOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {item.editedBy || '-'}
                      </Typography>
                    </Stack>
                  </Stack>
                </TableCell>
                <TableCell scope="row">{renderStatusChip(item)}</TableCell>
                <TableCell scope="row">
                  <Stack spacing={0.5} sx={{ minWidth: 520, whiteSpace: 'nowrap' }}>
                    <div>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        {renderChangeLabel(strings.bookingHistoryTabsGuests)}
                        <div style={{ flex: 1 }}>
                          {(() => {
                            if (hasSnapshots(item)) {
                              const { oldSnap, newSnap } = getSnapshots(item);
                              return renderSnapshotDiff(oldSnap?.guaranteedGuests, newSnap?.guaranteedGuests);
                            }
                            return renderValueDiff({
                              isUpdated: item.isGuestsUpdated,
                              oldValue: item.oldGuaranteedGuests,
                              newValue: item.newGuaranteedGuests,
                            });
                          })()}
                        </div>
                      </Stack>
                    </div>

                    <div>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        {renderChangeLabel(
                          bookingLineItem.hourlyPricing
                            ? strings.bookingHistoryTabsPricePerHour
                            : strings.bookingHistoryTabsPricePerPerson
                        )}
                        <div style={{ flex: 1 }}>
                          {(() => {
                            if (hasSnapshots(item)) {
                              const { oldSnap, newSnap } = getSnapshots(item);
                              if (bookingLineItem.hourlyPricing) {
                                return renderSnapshotDiff(oldSnap?.pricePerHour, newSnap?.pricePerHour);
                              }
                              return renderSnapshotDiff(oldSnap?.pricePerPerson, newSnap?.pricePerPerson);
                            }
                            if (bookingLineItem.hourlyPricing) {
                              return renderValueDiff({
                                isUpdated: item.isPricePerHourUpdated,
                                oldValue: item.formattedOldPricePerHour,
                                newValue: item.formattedNewPricePerHour,
                              });
                            }
                            return renderValueDiff({
                              isUpdated: item.isPriceUpdated,
                              oldValue: item.formattedOldPricePerPerson,
                              newValue: item.formattedNewPricePerPerson,
                            });
                          })()}
                        </div>
                      </Stack>
                    </div>

                    {!bookingLineItem.hourlyPricing && (
                      <div>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          {renderChangeLabel(strings.bookingHistoryTabsMinimumRevenue)}
                          <div style={{ flex: 1 }}>
                            {(() => {
                              if (hasSnapshots(item)) {
                                const { oldSnap, newSnap } = getSnapshots(item);
                                return renderSnapshotDiff(oldSnap?.minimumRevenue, newSnap?.minimumRevenue);
                              }
                              return renderValueDiff({
                                isUpdated: item.isMinimumRevenueUpdated,
                                oldValue: item.formattedOldMinimumRevenue,
                                newValue: item.formattedNewMinimumRevenue,
                              });
                            })()}
                          </div>
                        </Stack>
                      </div>
                    )}

                    <div>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        {renderChangeLabel(strings.bookingHistoryTabsHallRental)}
                        <div style={{ flex: 1 }}>
                          {(() => {
                            if (hasSnapshots(item)) {
                              const { oldSnap, newSnap } = getSnapshots(item);
                              return renderSnapshotDiff(oldSnap?.hallRental, newSnap?.hallRental);
                            }
                            return renderValueDiff({
                              isUpdated: item.isHallRentalUpdated,
                              oldValue: item.formattedOldHallRental,
                              newValue: item.formattedNewHallRental,
                            });
                          })()}
                        </div>
                      </Stack>
                    </div>

                    <div>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        {renderChangeLabel(strings.bookingHistoryTabsActualCost)}
                        <div style={{ flex: 1 }}>
                          {(() => {
                            if (!hasSnapshots(item)) return '-';
                            const { oldSnap, newSnap } = getSnapshots(item);
                            return renderSnapshotDiff(oldSnap?.actualTotalCost, newSnap?.actualTotalCost);
                          })()}
                        </div>
                      </Stack>
                    </div>
                  </Stack>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'break-spaces' }} scope="row">
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <div style={{ flex: 1 }}>{item.updateReason || '-'}</div>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setCompareItem(item);
                        setCompareOpen(true);
                      }}
                      disabled={!item?.oldSnapshotJson && !item?.newSnapshotJson}
                      title="Compare"
                    >
                      <CompareArrowsRoundedIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </TableCell>
              </Fragment>
            </TableRow>
          ))}
        </Tabular>
      ) : (
        <Typography variant="h5" sx={{ my: 2 }}>
          {' '}
          {strings.bookingHistoryTabsHistoryNotFound}
        </Typography>
      )}

      <DrawerMenu
        title="Audit Comparison"
        width="min(1012px, 100vw)"
        closeButton
        open={compareOpen}
        handleClose={() => {
          setCompareOpen(false);
          setCompareItem(null);
        }}>
        <div style={{ width: '100%', minWidth: 0 }}>
          <Stack spacing={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={!showAllCompareFields}
                  onChange={(_, checked) => setShowAllCompareFields(!checked)}
                  size="small"
                />
              }
              label={<Typography variant="caption">Show only changes</Typography>}
            />
          </Stack>
          {!compareData ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No snapshot available.
            </Typography>
          ) : (
            (() => {
              const oldSnap = (compareData.oldSnap as any) ?? {};
              const newSnap = (compareData.newSnap as any) ?? {};
              const summary = summarizeChanges(oldSnap, newSnap);

              const renderField = (label: string, oldVal: any, newVal: any, changed: boolean, body: React.ReactNode) => {
                if (!showAllCompareFields && !changed) return null;
                if (!changed && isMeaningfullyEmpty(oldVal) && isMeaningfullyEmpty(newVal)) return null;
                return (
                  <div>
                    <Typography variant="caption" sx={{ fontWeight: 900, color: 'text.secondary' }}>
                      {label}
                    </Typography>
                    <div style={{ marginTop: 4 }}>{body}</div>
                  </div>
                );
              };

              const pricingPriceOld = bookingLineItem?.hourlyPricing ? oldSnap.pricePerHour : oldSnap.pricePerPerson;
              const pricingPriceNew = bookingLineItem?.hourlyPricing ? newSnap.pricePerHour : newSnap.pricePerPerson;
              const priceDiff = inlineDiff(pricingPriceOld, pricingPriceNew);
              const minRevDiff = inlineDiff(oldSnap.minimumRevenue, newSnap.minimumRevenue);
              const hallDiff = inlineDiff(oldSnap.hallRental, newSnap.hallRental);
              const scDiff = inlineDiff(oldSnap.serviceCharge, newSnap.serviceCharge);
              const costDiff = inlineDiff(oldSnap.actualTotalCost, newSnap.actualTotalCost);

              const guestsDiff = inlineDiff(oldSnap.guaranteedGuests, newSnap.guaranteedGuests);
              const layoutChanged = changedLayout(oldSnap.layout, newSnap.layout);
              const layoutBody = inlineDiff(formatLayout(oldSnap.layout), formatLayout(newSnap.layout));
              const eventSubtypeDiff = inlineDiff(oldSnap.eventSubtype, newSnap.eventSubtype);

              const startDiff = inlineDiff(
                `${oldSnap.startDate ?? '-'} ${oldSnap.startTime ?? ''}`.trim(),
                `${newSnap.startDate ?? '-'} ${newSnap.startTime ?? ''}`.trim()
              );
              const endDiff = inlineDiff(
                `${oldSnap.endDate ?? '-'} ${oldSnap.endTime ?? ''}`.trim(),
                `${newSnap.endDate ?? '-'} ${newSnap.endTime ?? ''}`.trim()
              );
              const podDiff = inlineDiff(oldSnap.partOfDay, newSnap.partOfDay);

              const guestTaxChanged = changedTaxes(oldSnap.taxOnGuestCharges, newSnap.taxOnGuestCharges);
              const hallTaxChanged = changedTaxes(oldSnap.taxOnHallRental, newSnap.taxOnHallRental);
              const guestTaxBody = inlineDiff(formatTaxes(oldSnap.taxOnGuestCharges), formatTaxes(newSnap.taxOnGuestCharges));
              const hallTaxBody = inlineDiff(formatTaxes(oldSnap.taxOnHallRental), formatTaxes(newSnap.taxOnHallRental));

              const addOnsChanged = changedAddOns(oldSnap.addOns, newSnap.addOns);

              const addOnsDiff = computeAddOnsDiff(oldSnap.addOns, newSnap.addOns);

              const addOnsCard = (
                <Stack spacing={1}>
                  {addOnsDiff.added.map((a, idx) => (
                    <Stack key={`a-${idx}`} direction="row" spacing={1} alignItems="center">
                      <Chip size="small" label="Added" sx={{ bgcolor: '#DBEAFE', color: '#1D4ED8', fontWeight: 800 }} />
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {addOnKey(a)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        qty: {a.quantity ?? '-'} • price: {a.price ?? '-'}
                      </Typography>
                    </Stack>
                  ))}

                  {addOnsDiff.removed.map((a, idx) => (
                    <Stack key={`r-${idx}`} direction="row" spacing={1} alignItems="center">
                      <Chip size="small" label="Removed" sx={{ bgcolor: '#FEE2E2', color: '#B91C1C', fontWeight: 800 }} />
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {addOnKey(a)}
                      </Typography>
                    </Stack>
                  ))}

                  {addOnsDiff.updated.map((u, idx) => (
                    <div key={`u-${idx}`}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" label="Updated" sx={{ bgcolor: '#FFEDD5', color: '#C2410C', fontWeight: 800 }} />
                        <Typography variant="body2" sx={{ fontWeight: 900 }}>
                          {addOnKey(u.newItem)}
                        </Typography>
                      </Stack>
                      <Stack spacing={0.5} sx={{ mt: 0.5, pl: 5.5 }}>
                        <Stack direction="row" spacing={1} alignItems="baseline">
                          <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 48 }}>
                            Qty:
                          </Typography>
                          {inlineDiff(u.oldItem.quantity, u.newItem.quantity).node}
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="baseline">
                          <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 48 }}>
                            Price:
                          </Typography>
                          {inlineDiff(u.oldItem.price, u.newItem.price).node}
                        </Stack>
                      </Stack>
                    </div>
                  ))}

                  {showAllCompareFields
                    ? addOnsDiff.unchanged.map((a, idx) => (
                        <Stack key={`n-${idx}`} direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {addOnKey(a)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            qty: {a.quantity ?? '-'} • price: {a.price ?? '-'}
                          </Typography>
                        </Stack>
                      ))
                    : null}

                  {!addOnsChanged && showAllCompareFields && addOnsDiff.unchanged.length === 0 ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      -
                    </Typography>
                  ) : null}
                </Stack>
              );

              return (
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                  <div>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <EditOutlinedIcon fontSize="small" color="action" /> Changes Summary
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      • {summary.totalFieldsUpdated} fields updated
                      {summary.pricingChanges ? ` • ${summary.pricingChanges} pricing changes` : ''}
                      {summary.layoutChanges ? ` • ${summary.layoutChanges} layout change` : ''}
                      {summary.addOnChanges ? ` • ${summary.addOnChanges} add-on modified` : ''}
                    </Typography>
                  </div>

                  {(() => {
                    const pricingFields = [
                      renderField(
                        bookingLineItem?.hourlyPricing ? strings.bookingHistoryTabsPricePerHour : strings.bookingHistoryTabsPricePerPerson,
                        pricingPriceOld,
                        pricingPriceNew,
                        priceDiff.changed,
                        priceDiff.node
                      ),
                      renderField(
                        strings.bookingHistoryTabsMinimumRevenue,
                        oldSnap.minimumRevenue,
                        newSnap.minimumRevenue,
                        minRevDiff.changed,
                        minRevDiff.node
                      ),
                      renderField(strings.bookingHistoryTabsHallRental, oldSnap.hallRental, newSnap.hallRental, hallDiff.changed, hallDiff.node),
                      renderField('Service Charge', oldSnap.serviceCharge, newSnap.serviceCharge, scDiff.changed, scDiff.node),
                      renderField(strings.bookingHistoryTabsActualCost, oldSnap.actualTotalCost, newSnap.actualTotalCost, costDiff.changed, costDiff.node),
                    ].filter(Boolean);

                    const eventFields = [
                      renderField('Layout', oldSnap.layout, newSnap.layout, layoutChanged, layoutBody.node),
                      renderField('Event Subtype', oldSnap.eventSubtype, newSnap.eventSubtype, eventSubtypeDiff.changed, eventSubtypeDiff.node),
                      renderField('Guests', oldSnap.guaranteedGuests, newSnap.guaranteedGuests, guestsDiff.changed, guestsDiff.node),
                    ].filter(Boolean);

                    const timingFields = [
                      renderField('Start', oldSnap.startDate, newSnap.startDate, startDiff.changed, startDiff.node),
                      renderField('End', oldSnap.endDate, newSnap.endDate, endDiff.changed, endDiff.node),
                      renderField('Part of Day', oldSnap.partOfDay, newSnap.partOfDay, podDiff.changed, podDiff.node),
                    ].filter(Boolean);

                    const taxFields = [
                      renderField('Tax on Guest Charges', oldSnap.taxOnGuestCharges, newSnap.taxOnGuestCharges, guestTaxChanged, guestTaxBody.node),
                      renderField('Tax on Hall Rental', oldSnap.taxOnHallRental, newSnap.taxOnHallRental, hallTaxChanged, hallTaxBody.node),
                    ].filter(Boolean);

                    const addOnVisible =
                      showAllCompareFields || addOnsChanged || !(isMeaningfullyEmpty(oldSnap.addOns) && isMeaningfullyEmpty(newSnap.addOns));

                    const sections: Array<React.ReactNode> = [];

                    if (pricingFields.length) {
                      sections.push(
                        <div key="pricing">
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <PaymentsOutlinedIcon fontSize="small" color="action" /> Pricing
                          </Typography>
                          <Stack spacing={1} sx={{ mt: 0.75 }}>
                            {pricingFields as any}
                          </Stack>
                        </div>
                      );
                    }

                    if (eventFields.length) {
                      sections.push(
                        <div key="event">
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <BusinessOutlinedIcon fontSize="small" color="action" /> Event Setup
                          </Typography>
                          <Stack spacing={1} sx={{ mt: 0.75 }}>
                            {eventFields as any}
                          </Stack>
                        </div>
                      );
                    }

                    if (timingFields.length) {
                      sections.push(
                        <div key="timing">
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <ScheduleOutlinedIcon fontSize="small" color="action" /> Timing
                          </Typography>
                          <Stack spacing={1} sx={{ mt: 0.75 }}>
                            {timingFields as any}
                          </Stack>
                        </div>
                      );
                    }

                    if (taxFields.length) {
                      sections.push(
                        <div key="tax">
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <ReceiptLongOutlinedIcon fontSize="small" color="action" /> Taxes
                          </Typography>
                          <Stack spacing={1} sx={{ mt: 0.75 }}>
                            {taxFields as any}
                          </Stack>
                        </div>
                      );
                    }

                    if (addOnVisible) {
                      const addOnContent =
                        !showAllCompareFields && !addOnsChanged
                          ? null
                          : !addOnsChanged && isMeaningfullyEmpty(oldSnap.addOns) && isMeaningfullyEmpty(newSnap.addOns)
                            ? null
                            : addOnsCard;

                      if (addOnContent) {
                        sections.push(
                          <div key="addons">
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <AddCircleOutlineRoundedIcon fontSize="small" color="action" /> Add-ons
                            </Typography>
                            <Stack spacing={1} sx={{ mt: 0.75 }}>
                              {addOnContent as any}
                            </Stack>
                          </div>
                        );
                      }
                    }

                    return sections;
                  })()}
                </Stack>
              );
            })()
          )}
        </div>
      </DrawerMenu>
    </>
  );
};
export default BookingHistory;
